import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { apiService } from '@/services/api';
import { Button } from '@/components/ui/button';
import MessageContent from '@/components/chat/MessageContent';
import BlueprintNotification from '@/components/chat/BlueprintNotification';
import SessionBlueprintsPanel from '@/components/chat/SessionBlueprintsPanel';
import SessionList from '@/components/chat/SessionList';
import type { ChatMessage, Blueprint } from '@/types';
import { Send, AlertCircle, PanelLeftClose, PanelLeft } from 'lucide-react';
import { apiFetch } from '../../lib/api';

const STORAGE_KEY = 'mema-chat-session';
const TEMP_USER_ID_KEY = 'mema-temp-user-id';

export default function ChatPage() {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string>();
  const [tempUserId, setTempUserId] = useState<string>();
  const [extractedBlueprint, setExtractedBlueprint] = useState<Blueprint | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isTempUserIdInitialized, setIsTempUserIdInitialized] = useState(false);
  const [_isPolling, setIsPolling] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pollingIntervalRef = useRef<number | null>(null);

  // Fetch session blueprints
  const { data: sessionBlueprints = [], isLoading: isBlueprintsLoading } = useQuery({
    queryKey: ['session-blueprints', sessionId],
    queryFn: () => apiService.getSessionBlueprints(sessionId!),
    enabled: !!sessionId,
    refetchInterval: false,
  });

  // Initialize temp user ID
  useEffect(() => {
    const storedTempUserId = localStorage.getItem(TEMP_USER_ID_KEY);
    if (storedTempUserId) {
      setTempUserId(storedTempUserId);
    }
    setIsTempUserIdInitialized(true);
  }, []);

  const chatMutation = useMutation({
    mutationFn: async (request: { message: string; sessionId?: string }) => {
      const response = await apiFetch('/ai/v1/skills/agent?stream=true', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // 送るのは新しい 1 通だけ。過去のやり取りはサーバーが
          // conversationId から足す。毎回こちらから全文を送ると、
          // 画面の配列とサーバーの記録という正が 2 つできる。
          messages: [{ role: 'user', content: request.message }],
          scenarioId: 'pilot',
          maxIterations: 10,
          // これが無いと会話は保存されない。以前は送っていなかったため
          // 履歴が 1 件も作られず、一覧も復元も空のままだった。
          persist: true,
          conversationId: request.sessionId || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(`Chat failed: ${response.statusText}`);
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let finalContent = '';
      let buffer = '';
      // サーバーが最初に流してくる会話 ID。これを拾えないと、
      // 次の発言がまた別の会話として始まる。
      let conversationId = request.sessionId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'tool_use' && event.toolName) {
              // Show tool usage as a status message
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant' && last.content.startsWith('🔧')) {
                  // Update existing tool status message
                  return [...prev.slice(0, -1), {
                    ...last,
                    content: `🔧 ${event.toolName}${event.toolResult ? ' ✓' : '...'}`,
                  }];
                }
                return [...prev, {
                  role: 'assistant' as const,
                  content: `🔧 ${event.toolName}...`,
                  timestamp: new Date().toISOString(),
                }];
              });
            }

            if (event.type === 'text' && event.text) {
              finalContent = event.text;
            }

            if (event.type === 'conversation' && event.conversationId) {
              conversationId = event.conversationId;
            }

            if (event.type === 'done' && event.content) {
              finalContent = event.content;
            }
          } catch { /* ignore parse errors */ }
        }
      }

      return {
        reply: finalContent,
        sessionId: conversationId || '',
        isComplete: false,
        newMessages: finalContent ? [{
          role: 'assistant' as const,
          content: finalContent,
          timestamp: new Date().toISOString(),
        }] : [],
      };
    },
    onSuccess: (data) => {
      // 如果有 newMessages 字段（包含所有新增消息，含内部讨论），使用它来更新界面
      if (data.newMessages && data.newMessages.length > 0) {
        // 过滤掉用户消息（已经在 handleSubmit 中乐观添加了），只添加 AI 回复和内部讨论
        const aiMessages = data.newMessages.filter((msg: ChatMessage) => msg.role !== 'user');

        setMessages((prev) => [
          ...prev,
          ...aiMessages.map((msg: ChatMessage) => ({
            role: msg.role as 'user' | 'assistant',
            content: msg.content,
            timestamp: msg.timestamp || new Date().toISOString(),
            agentRole: msg.agentRole,
            isInternalDiscussion: msg.isInternalDiscussion,
          })),
        ]);
      } else {
        // 向后兼容：如果没有 newMessages，使用传统方式
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: data.reply,
            timestamp: new Date().toISOString(),
          },
        ]);
      }

      if (data.sessionId) setSessionId(data.sessionId);
      // TODO: blueprint extraction is now handled by agent tools (save_blueprint)
      // Future: detect blueprint in agent response and show notification
      // Invalidate sessions query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  // Restore session from localStorage and backend on component mount
  useEffect(() => {
    // Wait for temp user ID initialization
    if (!isTempUserIdInitialized) {
      return;
    }

    const restoreSession = async () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          setIsLoadingSession(false);
          return;
        }

        const { sessionId: storedSessionId } = JSON.parse(stored);
        if (!storedSessionId) {
          setIsLoadingSession(false);
          return;
        }

        // Fetch full session from backend
        const session = await apiService.getSession(storedSessionId, tempUserId);

        // Filter out system messages and convert to ChatMessage format
        const restoredMessages: ChatMessage[] = session.messages
          .filter((msg) => msg.role !== 'system')
          .map((msg) => ({
            role: msg.role as 'user' | 'assistant',
            content: msg.content,
            timestamp: msg.timestamp || new Date().toISOString(),
            agentRole: msg.agentRole,
            isInternalDiscussion: msg.isInternalDiscussion,
          }));

        setMessages(restoredMessages);
        setSessionId(storedSessionId);
      } catch (error) {
        console.error('Failed to restore session:', error);
        // Clear invalid session from localStorage
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setIsLoadingSession(false);
      }
    };

    restoreSession();
  }, [isTempUserIdInitialized, tempUserId]);

  // Save session to localStorage whenever messages or sessionId changes
  useEffect(() => {
    if (sessionId && messages.length > 0) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          sessionId,
          lastUpdated: new Date().toISOString(),
        })
      );
    }
  }, [sessionId, messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for session updates when chat mutation is pending (for real-time internal discussion)
  useEffect(() => {
    if (chatMutation.isPending && sessionId && tempUserId) {
      setIsPolling(true);
      pollingIntervalRef.current = window.setInterval(async () => {
        try {
          const session = await apiService.getSession(sessionId, tempUserId);
          const latestMessages: ChatMessage[] = session.messages
            .filter((msg) => msg.role !== 'system')
            .map((msg) => ({
              role: msg.role as 'user' | 'assistant',
              content: msg.content,
              timestamp: msg.timestamp || new Date().toISOString(),
              agentRole: msg.agentRole,
              isInternalDiscussion: msg.isInternalDiscussion,
            }));
          setMessages(latestMessages);
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 2000); // Poll every 2 seconds
    } else {
      // Clear polling when mutation is complete
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
    }

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [chatMutation.isPending, sessionId, tempUserId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString(),
    };

    // Optimistic update: immediately show user message
    setMessages((prev) => [...prev, userMessage]);
    chatMutation.mutate({ message: input, sessionId });
    setInput('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter without Shift sends the message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handleSessionSelect = async (newSessionId: string) => {
    if (newSessionId === sessionId) return;

    try {
      setIsLoadingSession(true);
      const session = await apiService.getSession(newSessionId, tempUserId);

      const restoredMessages: ChatMessage[] = session.messages
        .filter((msg) => msg.role !== 'system')
        .map((msg) => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.content,
          timestamp: msg.timestamp || new Date().toISOString(),
          agentRole: msg.agentRole,
          isInternalDiscussion: msg.isInternalDiscussion,
        }));

      setMessages(restoredMessages);
      setSessionId(newSessionId);
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          sessionId: newSessionId,
          lastUpdated: new Date().toISOString(),
        })
      );
    } catch (error) {
      console.error('Failed to load session:', error);
    } finally {
      setIsLoadingSession(false);
    }
  };

  const handleNewSession = () => {
    setMessages([]);
    setSessionId(undefined);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      <div className="flex h-full">
        {/* Sidebar */}
        {showSidebar && (
          <div className="w-80 border-r">
            <SessionList
              currentSessionId={sessionId}
              tempUserId={tempUserId}
              onSessionSelect={handleSessionSelect}
              onNewSession={handleNewSession}
            />
          </div>
        )}

        {/* Main Chat Area */}
        <div className="flex flex-1 flex-col">
          {/* Header with sidebar toggle and blueprints panel */}
          <div className="border-b bg-background p-2 flex items-center justify-between">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowSidebar(!showSidebar)}
              className="h-8 px-2"
            >
              {showSidebar ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeft className="h-4 w-4" />
              )}
            </Button>

            {/* Session blueprints panel */}
            {sessionId && sessionBlueprints.length > 0 && (
              <SessionBlueprintsPanel
                blueprints={sessionBlueprints}
                isLoading={isBlueprintsLoading}
              />
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="mx-auto max-w-3xl space-y-4">
              {isLoadingSession ? (
                <div className="flex h-full items-center justify-center text-center text-muted-foreground">
                  <div>
                    <p className="text-lg">加载会话中...</p>
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex h-full items-center justify-center text-center text-muted-foreground">
                  <div>
                    <p className="text-lg">开始与 AI 助手对话</p>
                    <p className="mt-2 text-sm">
                      描述您的项目需求，我会帮您创建需求蓝图
                    </p>
                  </div>
                </div>
              ) : (
                messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-3 ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : message.isInternalDiscussion
                          ? 'bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-500'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      <MessageContent
                        content={message.content}
                        role={message.role}
                        agentRole={message.agentRole}
                        isInternalDiscussion={message.isInternalDiscussion}
                      />
                    </div>
                  </div>
                ))
              )}
              {chatMutation.isPending && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg bg-muted px-4 py-2">
                    <p className="text-muted-foreground">思考中...</p>
                  </div>
                </div>
              )}
              {chatMutation.isError && (
                <div className="flex justify-center">
                  <div className="flex items-center gap-2 rounded-lg bg-destructive/10 px-4 py-2 text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    <p className="text-sm">发送消息失败，请重试</p>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <div className="border-t bg-background p-4">
            <form onSubmit={handleSubmit} className="mx-auto flex max-w-3xl gap-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="描述您的项目需求..."
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-hidden min-h-[40px] max-h-[200px]"
                disabled={chatMutation.isPending}
                rows={1}
              />
              <Button type="submit" disabled={chatMutation.isPending || !input.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Blueprint extraction notification */}
      {extractedBlueprint && (
        <BlueprintNotification
          blueprint={extractedBlueprint}
          onClose={() => setExtractedBlueprint(null)}
        />
      )}
    </>
  );
}
